/* eslint-disable react-refresh/only-export-components --
 * `setBreadcrumbLabel` / `useBreadcrumbLabel` are the breadcrumb's own label
 * registry (below) and belong with the component that reads it. The house
 * pattern for non-component exports is a sibling `.ts` module (cf.
 * `allocation/allocation-scope-context.ts`, `lib/policies/badges.ts`); doing that
 * here means a new shared-zone file, which is outside this slice's ownership, so
 * the cost is paid as a documented fast-refresh exemption instead: editing this
 * file full-reloads in dev rather than hot-swapping. Split it out when the
 * registry next needs to grow.
 */
import { Fragment, useEffect, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

// URN-aware breadcrumb per docs/ui/operator-ui-tds.md §4.3.
// Example: /cases/urn:case:abc/allocation → "Cases / urn:case:abc / Allocation"
// Each non-final crumb is a Link back to the parent path; the leaf is plain text.
//
// NEX-299 (S25) — friendly labels beat URNs (frontend-conventions §18.3). A URN
// path segment used to render as the crumb's PRIMARY text, so the disruption
// detail page read `Disruptions › urn:disruption:oquvhfdxso59` — a raw URN as the
// main navigation label, which §18.3 forbids ("URNs are reference only … never a
// primary title"). A route that already holds the entity can now name it via
// `useBreadcrumbLabel(urn, friendlyLabel)`; the crumb then renders the human
// label and keeps the URN as the hover/`title` reference.
//
// The registry is a module-level store read with `useSyncExternalStore` — no
// props, no context, no fetch. `<Breadcrumb />` keeps its exact signature, so the
// existing caller (`topbar.tsx`) is untouched. A URN with no registered label
// falls back to the previous monospace-URN rendering.

const labels = new Map<string, string>();
const listeners = new Set<() => void>();
let version = 0;

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function snapshot(): number {
  return version;
}

/**
 * Register (or clear, with `undefined`) the friendly label for a URN crumb.
 * Imperative escape hatch for non-component code; components should prefer
 * `useBreadcrumbLabel`, which cleans up on unmount.
 */
export function setBreadcrumbLabel(urn: string, label: string | undefined): void {
  if (label) {
    if (labels.get(urn) === label) return;
    labels.set(urn, label);
  } else {
    if (!labels.has(urn)) return;
    labels.delete(urn);
  }
  emit();
}

/**
 * Name a URN crumb from the route that already owns the entity — e.g. the
 * disruption detail page passes the disruption's title, so the breadcrumb reads
 * `Disruptions › S21 verify` instead of `Disruptions › urn:disruption:…`.
 *
 * This is a pure cache write from data the caller ALREADY has: it never fetches.
 * The label is dropped when the route unmounts so a stale name can't outlive it.
 */
export function useBreadcrumbLabel(urn: string | undefined, label: string | undefined): void {
  useEffect(() => {
    if (!urn || !label) return;
    setBreadcrumbLabel(urn, label);
    return () => setBreadcrumbLabel(urn, undefined);
  }, [urn, label]);
}

function isUrnSegment(segment: string): boolean {
  return segment.startsWith('urn:');
}

function humanize(segment: string): string {
  if (isUrnSegment(segment)) return segment;
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Route segments the chrome names for itself. Holds KEYS, never language — a
// `common:` prefix reuses the shared nav vocabulary. A segment that is not
// listed (a param, a surface added later) falls back to `humanize`, so the
// breadcrumb never renders a raw key.
const SEGMENT_KEY: Record<string, string> = {
  dashboard: 'common:nav.dashboard',
  cases: 'common:nav.cases',
  historic: 'breadcrumb.segment.historic',
  new: 'breadcrumb.segment.new',
  disruptions: 'sidebar.nav.disruptions',
  tower: 'breadcrumb.segment.tower',
  classic: 'breadcrumb.segment.classic',
  express: 'breadcrumb.segment.express',
  flights: 'sidebar.nav.flights',
  reservations: 'sidebar.nav.reservations',
  hotels: 'breadcrumb.segment.hotels',
  history: 'breadcrumb.segment.history',
  integrations: 'reservations:integrations.tab',
  passengers: 'sidebar.nav.passengers',
  imports: 'breadcrumb.segment.imports',
  formats: 'sidebar.nav.formats',
  'manual-review': 'breadcrumb.segment.manualReview',
  providers: 'sidebar.nav.providers',
  'provider-sim': 'sidebar.nav.providerSim',
  policies: 'common:nav.policies',
  explorer: 'breadcrumb.segment.explorer',
  simulator: 'breadcrumb.segment.simulator',
  compare: 'breadcrumb.segment.compare',
  edit: 'breadcrumb.segment.edit',
  contracts: 'common:nav.contracts',
  users: 'sidebar.nav.users',
  profile: 'common:nav.profile',
  billing: 'common:nav.billing',
  reports: 'common:nav.reports',
  health: 'breadcrumb.segment.health',
  allocation: 'breadcrumb.segment.allocation',
};

function segmentLabel(segment: string, t: TFunction<'admin'>): string {
  const key = SEGMENT_KEY[segment];
  return key ? t(key) : humanize(segment);
}

interface Crumb {
  href: string;
  /** What the operator reads — the friendly label when one is registered. */
  label: string;
  /** The full URN, kept as the hover reference when this crumb is URN-shaped. */
  urn?: string;
  /** True only when the crumb still falls back to rendering the raw URN. */
  isUrn: boolean;
}

function buildCrumbs(pathname: string, t: TFunction<'admin'>): readonly Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [];
  let acc = '';
  for (const segment of segments) {
    acc += `/${segment}`;
    const decoded = decodeURIComponent(segment);
    const urn = isUrnSegment(decoded) ? decoded : undefined;
    // A registered friendly label wins over the URN (§18.3); with none, the crumb
    // degrades to the previous verbatim-URN rendering rather than showing nothing.
    const friendly = labels.get(decoded);
    crumbs.push({
      href: acc,
      label: friendly ?? (urn ? decoded : segmentLabel(decoded, t)),
      ...(urn ? { urn } : {}),
      isUrn: !!urn && !friendly,
    });
  }
  return crumbs;
}

export function Breadcrumb() {
  const { t } = useTranslation('admin');
  const { pathname } = useLocation();
  // Re-render when a route registers/clears a friendly label for a URN crumb.
  useSyncExternalStore(subscribe, snapshot, snapshot);
  const crumbs = buildCrumbs(pathname, t);

  if (crumbs.length === 0) {
    return null;
  }

  return (
    <nav aria-label={t('breadcrumb.label')} className="flex items-center gap-1 overflow-hidden text-xs">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <Fragment key={crumb.href}>
            {i > 0 ? (
              <ChevronRight className="h-3 w-3 text-muted-foreground" aria-hidden />
            ) : null}
            {isLast ? (
              <span
                aria-current="page"
                className={cn(
                  'truncate text-foreground',
                  crumb.isUrn && 'max-w-[16rem] font-mono',
                )}
                title={crumb.urn}
              >
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.href}
                className={cn(
                  'truncate text-muted-foreground hover:text-foreground',
                  crumb.isUrn && 'max-w-[16rem] font-mono',
                )}
                title={crumb.urn}
              >
                {crumb.label}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
